import { useCallback } from 'react';
import { useLanguageContext } from '../contexts/LanguageContext';
import { useUITranslations } from './useUITranslations';
import appConfig from '../data/app-config.json';

export interface UITexts {
  navigation: any;
  homeScreen: any;
  booksScreen: any;
  heroesScreen: any;
  collectionScreen: any;
  worldScreen: any;
  readingScreen: any;
}

export interface AppConfig {
  app: any;
  gradients: any;
  screens: any;
  animations: any;
  premium: any;
}

export const useAppData = () => {
  const { language, translate } = useLanguageContext();
  const { translations: uiTexts, loading: translationsLoading } = useUITranslations();

  // Si les traductions ne sont pas chargées, ne rien construire
  if (translationsLoading || !uiTexts || Object.keys(uiTexts).length === 0) {
    return {
      uiTexts: {},
      config: appConfig as AppConfig,
      translationsLoading: true,
      getUIText: () => '',
      getConfig: (path: string) => null,
      language,
      translate,
      gradients: appConfig.gradients,
      screenConfig: appConfig.screens,
      supportedLanguages: appConfig.app.supportedLanguages,
      nav: { back: '', home: '' },
      homeTexts: {
        carousel: [],
        menuButtons: {
          books: { label: '', icon: '' },
          collection: { label: '', icon: '' },
          heroes: { label: '', icon: '' },
          world: { label: '', icon: '' },
          settings: { label: '', icon: '' }
        }
      },
      booksTexts: {
        title: '',
        tabs: { preview: '', all: '' },
        weeklyBooks: { title: '', description: '' },
        allBooksSection: { title: '', premiumButton: '' },
        objectsLabel: ''
      },
      heroesTexts: {
        title: '',
        subtitle: '',
        description: '',
        status: { selected: '', current: '', choose: '', bookRequired: '' }
      },
      collectionTexts: {
        title: '',
        subtitle: '',
        description: '',
        status: { collected: '', addToBackground: '' },
        getConfirmReadText: () => ''
      },
      worldTexts: {
        editMode: { label: '', description: '' },
        objectPanel: { title: '' }
      },
      readingTexts: {
        loading: '',
        chooseYourPath: '',
        theEnd: '',
        rewardsUnlocked: ''
      },
      modalTexts: {
        guestLimit: {
          rewards: { icon: '', title: '', description: '', benefits: [] },
          collection: { icon: '', title: '', description: '', benefits: [] },
          heroes: { icon: '', title: '', description: '', benefits: [] },
          world: { icon: '', title: '', description: '', benefits: [] },
          transformation: { icon: '', title: '', description: '', benefits: [] },
          buttons: { createAccount: '', login: '', later: '' },
          footer: ''
        },
        subscription: {
          guest: { title: '', subtitle: '' },
          premium: { title: '', subtitle: '' },
          features: {
            allBooks: { title: '', description: '' },
            offline: { title: '', description: '' },
            unlimitedCollection: { title: '', description: '' },
            rareObjects: { title: '', description: '' }
          },
          pricing: { monthly: '', subscription: '', cancelAnytime: '', noCommitment: '' },
          buttons: { createAccount: '', login: '', subscribe: '', later: '', comingSoon: '' },
          footer: { guest: '', premium: '' }
        }
      }
    };
  }

  // Fonction pour traduire un objet de textes UI
  const getUIText = (path: string) => {
    const keys = path.split('.');
    let current: any = uiTexts;

    for (const key of keys) {
      current = current[key];
      if (!current) return '';
    }

    return translate(current);
  };

  // Fonction pour obtenir la configuration
  const getConfig = (path: string) => {
    const keys = path.split('.');
    let current: any = appConfig;
    
    for (const key of keys) {
      current = current[key];
      if (!current) return null;
    }
    
    return current;
  };

  return {
    // Données brutes
    uiTexts: uiTexts as UITexts,
    config: appConfig as AppConfig,

    // Loading states
    translationsLoading,

    // Fonctions utilitaires
    getUIText,
    getConfig,
    language,
    translate,

    // Raccourcis pour les données fréquemment utilisées
    gradients: appConfig.gradients,
    screenConfig: appConfig.screens,
    supportedLanguages: appConfig.app.supportedLanguages,
    
    // Textes traduits pour les écrans principaux
    nav: {
      back: translate(uiTexts.navigation?.back),
      home: translate(uiTexts.navigation?.home)
    },
    
    homeTexts: {
      carousel: uiTexts?.homeScreen?.carousel?.[language] || [],
      menuButtons: uiTexts?.homeScreen?.menuButtons
        ? Object.keys(uiTexts.homeScreen.menuButtons).reduce((acc: any, key: string) => {
            const button = (uiTexts.homeScreen.menuButtons as any)[key];
            acc[key] = {
              label: translate(button.label),
              icon: button.icon
            };
            return acc;
          }, {})
        : {}
    },

    booksTexts: {
      title: translate(uiTexts.booksScreen?.title),
      tabs: {
        preview: translate(uiTexts.booksScreen?.tabs?.preview),
        all: translate(uiTexts.booksScreen?.tabs?.all)
      },
      weeklyBooks: {
        title: translate(uiTexts.booksScreen?.weeklyBooks?.title),
        description: translate(uiTexts.booksScreen?.weeklyBooks?.description)
      },
      allBooksSection: {
        title: translate(uiTexts.booksScreen?.allBooksSection?.title),
        premiumButton: translate(uiTexts.booksScreen?.allBooksSection?.premiumButton)
      },
      objectsLabel: translate(uiTexts.booksScreen?.objectsLabel)
    },

    heroesTexts: {
      title: translate(uiTexts.heroesScreen?.title),
      subtitle: translate(uiTexts.heroesScreen?.subtitle),
      description: translate(uiTexts.heroesScreen?.description),
      status: {
        selected: translate(uiTexts.heroesScreen?.status?.selected),
        current: translate(uiTexts.heroesScreen?.status?.current),
        choose: translate(uiTexts.heroesScreen?.status?.choose),
        bookRequired: translate(uiTexts.heroesScreen?.status?.bookRequired)
      }
    },

    collectionTexts: {
      title: translate(uiTexts.collectionScreen?.title),
      subtitle: translate(uiTexts.collectionScreen?.subtitle),
      description: translate(uiTexts.collectionScreen?.description),
      status: {
        collected: translate(uiTexts.collectionScreen?.status?.collected),
        addToBackground: translate(uiTexts.collectionScreen?.status?.addToBackground)
      },
      getConfirmReadText: (bookTitle: string, objectName: string) => {
        const template = translate(uiTexts.collectionScreen?.confirmRead);
        return template?.replace('{bookTitle}', bookTitle).replace('{objectName}', objectName) || '';
      }
    },

    worldTexts: {
      editMode: {
        label: translate(uiTexts.worldScreen?.editMode?.label),
        description: translate(uiTexts.worldScreen?.editMode?.description)
      },
      objectPanel: {
        title: translate(uiTexts.worldScreen?.objectPanel?.title)
      }
    },

    readingTexts: {
      loading: translate(uiTexts.readingScreen?.loading),
      chooseYourPath: translate(uiTexts.readingScreen?.chooseYourPath),
      theEnd: translate(uiTexts.readingScreen?.theEnd),
      rewardsUnlocked: translate(uiTexts.readingScreen?.rewardsUnlocked)
    },

    // Modal translations
    modalTexts: {
      guestLimit: {
        rewards: {
          icon: (uiTexts?.modals?.guestLimit?.rewards as any)?.icon || '',
          title: translate((uiTexts?.modals?.guestLimit?.rewards as any)?.title),
          description: translate((uiTexts?.modals?.guestLimit?.rewards as any)?.description),
          benefits: Array.isArray((uiTexts?.modals?.guestLimit?.rewards as any)?.benefits)
            ? (uiTexts.modals.guestLimit.rewards as any).benefits.map((b: any) => translate(b))
            : []
        },
        collection: {
          icon: (uiTexts?.modals?.guestLimit?.collection as any)?.icon || '',
          title: translate((uiTexts?.modals?.guestLimit?.collection as any)?.title),
          description: translate((uiTexts?.modals?.guestLimit?.collection as any)?.description),
          benefits: Array.isArray((uiTexts?.modals?.guestLimit?.collection as any)?.benefits)
            ? (uiTexts.modals.guestLimit.collection as any).benefits.map((b: any) => translate(b))
            : []
        },
        heroes: {
          icon: (uiTexts?.modals?.guestLimit?.heroes as any)?.icon || '',
          title: translate((uiTexts?.modals?.guestLimit?.heroes as any)?.title),
          description: translate((uiTexts?.modals?.guestLimit?.heroes as any)?.description),
          benefits: Array.isArray((uiTexts?.modals?.guestLimit?.heroes as any)?.benefits)
            ? (uiTexts.modals.guestLimit.heroes as any).benefits.map((b: any) => translate(b))
            : []
        },
        world: {
          icon: (uiTexts?.modals?.guestLimit?.world as any)?.icon || '',
          title: translate((uiTexts?.modals?.guestLimit?.world as any)?.title),
          description: translate((uiTexts?.modals?.guestLimit?.world as any)?.description),
          benefits: Array.isArray((uiTexts?.modals?.guestLimit?.world as any)?.benefits)
            ? (uiTexts.modals.guestLimit.world as any).benefits.map((b: any) => translate(b))
            : []
        },
        transformation: {
          icon: (uiTexts?.modals?.guestLimit?.transformation as any)?.icon || '',
          title: translate((uiTexts?.modals?.guestLimit?.transformation as any)?.title),
          description: translate((uiTexts?.modals?.guestLimit?.transformation as any)?.description),
          benefits: Array.isArray((uiTexts?.modals?.guestLimit?.transformation as any)?.benefits)
            ? (uiTexts.modals.guestLimit.transformation as any).benefits.map((b: any) => translate(b))
            : []
        },
        buttons: {
          createAccount: translate((uiTexts?.modals?.guestLimit?.buttons as any)?.createAccount),
          login: translate((uiTexts?.modals?.guestLimit?.buttons as any)?.login),
          later: translate((uiTexts?.modals?.guestLimit?.buttons as any)?.later)
        },
        footer: translate((uiTexts?.modals?.guestLimit as any)?.footer)
      },
      subscription: {
        guest: {
          title: translate((uiTexts?.modals?.subscription?.guest as any)?.title),
          subtitle: translate((uiTexts?.modals?.subscription?.guest as any)?.subtitle)
        },
        premium: {
          title: translate((uiTexts?.modals?.subscription?.premium as any)?.title),
          subtitle: translate((uiTexts?.modals?.subscription?.premium as any)?.subtitle)
        },
        features: {
          allBooks: {
            title: translate((uiTexts?.modals?.subscription?.features?.allBooks as any)?.title),
            description: translate((uiTexts?.modals?.subscription?.features?.allBooks as any)?.description)
          },
          offline: {
            title: translate((uiTexts?.modals?.subscription?.features?.offline as any)?.title),
            description: translate((uiTexts?.modals?.subscription?.features?.offline as any)?.description)
          },
          unlimitedCollection: {
            title: translate((uiTexts?.modals?.subscription?.features?.unlimitedCollection as any)?.title),
            description: translate((uiTexts?.modals?.subscription?.features?.unlimitedCollection as any)?.description)
          },
          rareObjects: {
            title: translate((uiTexts?.modals?.subscription?.features?.rareObjects as any)?.title),
            description: translate((uiTexts?.modals?.subscription?.features?.rareObjects as any)?.description)
          }
        },
        pricing: {
          monthly: translate((uiTexts?.modals?.subscription?.pricing as any)?.monthly),
          subscription: translate((uiTexts?.modals?.subscription?.pricing as any)?.subscription),
          cancelAnytime: translate((uiTexts?.modals?.subscription?.pricing as any)?.cancelAnytime),
          noCommitment: translate((uiTexts?.modals?.subscription?.pricing as any)?.noCommitment)
        },
        buttons: {
          createAccount: translate((uiTexts?.modals?.subscription?.buttons as any)?.createAccount),
          login: translate((uiTexts?.modals?.subscription?.buttons as any)?.login),
          subscribe: translate((uiTexts?.modals?.subscription?.buttons as any)?.subscribe),
          later: translate((uiTexts?.modals?.subscription?.buttons as any)?.later),
          comingSoon: translate((uiTexts?.modals?.subscription?.buttons as any)?.comingSoon)
        },
        footer: {
          guest: translate((uiTexts?.modals?.subscription?.footer as any)?.guest),
          premium: translate((uiTexts?.modals?.subscription?.footer as any)?.premium)
        }
      }
    }
  };
};